import React from "react";
import styled, { ThemeProvider } from "styled-components";
import { getSpaceThumbnail } from "@lifeat/shared/spaces/SpacesUtil";
import { useUserContext } from "@contexts/UserContext";
import { lightTheme } from "@themes/Theme";
import DynamicLobbyInfoCard from "../../features/room/DynamicLobbyInfoCard";
import DynamicLobbyInfoCardLayout from "../../features/room/DynamicLobbyInfoCardLayout";
import LoadingView from "../../features/virtual-space/components/SpaceView/LoadingView/LoadingView";
import useSpacePlayerController from "../../features/virtual-space/useSpacePlayerController";

const Root = styled.div`
    height: 100vh;
    overflow: scroll;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
    padding: 16px;
    color: ${({ theme }) => theme.textColorPrimary};
`;

const LOBBY_ROOM_IDS = [
    // PROD
    "CEq6YvdktFpNZa0v", // co-working cafe
    "qA7htCIRVGU0Oy3E", // Study Session
    "NIzD64tFer9N2QsN", // Late Night Crew
    "owrz04FLMFWAddpj", // Focus with Ela
    "3PPdo5RV1Q7XWiFk", // E's room
    "fgEwJImsMvePqoxM", // Study Squad by Reeda
    // TEST
    "HaOgkQzrKkUEn4xF",
    "dtOWRB3ACOCsZwla",
    "uEH5n8BIxUbS3mhk",
    "O3ZrsiVuVKgY6sZI",
    "1VdFGxOtamt8rgqK",
    "4X6bvWWgwEhnxM6J",
];

const STATIC_FALLBACK = [
    {
        id: "qA7htCIRVGU0Oy3E",
        roomName: "Study Session",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/study_session.png",
        roomDescription:
            "Your cozy space to chill & study with friends. Feel free to join the group pomodoro & list your tasks in chat.",
    },
    {
        id: "CEq6YvdktFpNZa0v",
        roomName: "Co-working Cafe",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/coworking_cafe.png",
        roomDescription: "Feel free to join the current group timer session & list your tasks in chat.",
    },
    {
        id: "NIzD64tFer9N2QsN",
        roomName: "Late Night Crew",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/late_night_crew.png",
        roomDescription:
            "Email: lilihackerrr@gmail.com Discord: SoldierGirl2162#7315\nWelcome to my library! Join the group pomodoro (30/5/25) and work with us. Keep us updated with your tasks and feel free to use the video chat! Please refrain from room offenses (posting links, spamming, offensive language, sensitive topics, etc). Good luck, brave ones. <3",
    },
    {
        id: "fgEwJImsMvePqoxM",
        roomName: "Study Squad",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/study_squad.png",
        roomDescription:
            "Welcome to your online study squad! 🤓 As a squad member, you're part of an international student community studying together. We help each other study better, plan for our careers, and make the most of university. 📚 I'm your host - Reeda, from @reedadesigns. I'm a PhD candidate and content creator! Don't forget to follow me on IG and tiktok 🥰",
    },
    {
        id: "owrz04FLMFWAddpj",
        roomName: "Focus with Ela",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/focus_with_ela.png",
        roomDescription:
            "Pomo is set to 50/10. At the start of every work timer, feel free to share your to-do list in the chat! :) \n\nStudy schedule~\nSaturday 9-11AM CDT (In this study room)\nMon-Fri 6:30-8AM CDT (In Discord StudySesh)",
    },
    {
        id: "gNRPeCyFCFkGarea",
        roomName: "\t🔆 Rise & Grind w/ Taylor 🔆",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/rise_and_grind.png",
        roomDescription:
            "The best place to start your morning! Grab your favorite drink and a snack and settle in for good vibes and productivity. ✨",
    },
    {
        id: "3PPdo5RV1Q7XWiFk",
        roomName: "E's Room",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/deep_work_with_e.png",
        roomDescription: "a focused space where effectivity & creativity flourish.",
    },
    {
        id: "WqUPrMBdDYgnuzj0",
        roomName: "Study with Souzen",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/study_with_souzen.png",
        roomDescription:
            "Hello and welcome to this space! I am so glad you could join me. Here I'll host weekly sessions so we can work hard and get stuff done together! You can check the calendar to see when I'll be hosting!\nThe pomodoro timer is set to 30 mins of work, 5 mins short break, and 15 min long break.",
    },
    {
        id: "aaBNYa8wYiJb42BH",
        roomName: "Let’s work with Soleyman 🚀",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/Lets_work_with_soleyman.png",
        roomDescription: "Let’s get our work done together and create the life we truly desire.🤝",
    },
    {
        id: "cvm5XH3FURFVeUnF",
        roomName: "The Productivity Nook",
        thumbnailUrl: "https://pub-45680305edfe47cbb1629fdb325587ec.r2.dev/the_productivity_nook.png",
        roomDescription: '"The bad news is time flies. The good news is you\'re the pilot." - Michael Altshuler',
    },
] as const;

export default function DynamicLobbyPage() {
    const { user, loading: userLoading } = useUserContext();

    const spaceController = useSpacePlayerController();

    if (userLoading) {
        return <LoadingView isAppLoading />;
    }
    return (
        <ThemeProvider theme={lightTheme}>
            <Root>
                <Grid>
                    {user
                        ? LOBBY_ROOM_IDS.map((roomId) => (
                              <DynamicLobbyInfoCard
                                  key={roomId}
                                  roomId={roomId}
                                  getSpaceThumbnail={(spaceId) =>
                                      getSpaceThumbnail(spaceController.getSpaceById(spaceId)?.videoUrl ?? "")
                                  }
                              />
                          ))
                        : STATIC_FALLBACK.map((fallback) => (
                              <DynamicLobbyInfoCardLayout
                                  key={fallback.id}
                                  roomLink={`https://lifeat.io/room/${fallback.id}`}
                                  thumbnailUrl={fallback.thumbnailUrl}
                                  roomName={fallback.roomName}
                                  roomDescription={fallback.roomDescription}
                                  guestCount={0}
                                  videoCallParticipantCount={0}
                              />
                          ))}
                </Grid>
            </Root>
        </ThemeProvider>
    );
}
